/**
 * useChainLeak — fetches /health/quarter-deltas (org-scoped) and
 * returns `[leak, refresh]` where `leak` is the chain stage with the
 * steepest quarter-over-quarter drop (the live "value leak"), or null
 * when no stage declined, and `refresh()` re-fetches on demand (e.g.
 * after seeding demo data) returning the recomputed leak.
 *
 * Shared by the per-page ChainPositionStrip and the guided tour so
 * the leak is detected in exactly one place.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { computeChainLeak } from '../lib/chainLeak';

export function useChainLeak() {
  const { viewAsOrg } = useAuth();
  const [leak, setLeak] = useState(null); // { key, metric, deltaPct, stage } | null

  const orgId = viewAsOrg?.id;

  const refresh = useCallback(async () => {
    const orgParam = orgId ? `?organization_id=${orgId}` : '';
    try {
      const res = await api.get(`/health/quarter-deltas${orgParam}`);
      const next = computeChainLeak(res?.data?.metrics);
      setLeak(next);
      return next;
    } catch (e) {
      logger.debug?.('useChainLeak fetch failed', e);
      return null;
    }
  }, [orgId]);

  useEffect(() => {
    let alive = true;
    const orgParam = orgId ? `?organization_id=${orgId}` : '';
    api.get(`/health/quarter-deltas${orgParam}`)
      .then((res) => { if (alive) setLeak(computeChainLeak(res?.data?.metrics)); })
      .catch((e) => { logger.debug?.('useChainLeak fetch failed', e); });
    return () => { alive = false; };
  }, [orgId]);

  return [leak, refresh];
}

export default useChainLeak;
